import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatBitsPerSecondAxis,
    formatTimeTick,
    throughputUnitToBitsPerSecondMultiplier,
} from "../../helpers/chart_common";
import {isRedacted} from "../../helpers/redact";
import {debugLog} from '../../helpers/debug_log';

export class ShaperSiteCombinedThroughput extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Site Child Throughput";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Site Combined Throughput</h5><p>Child site throughput.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperSiteCombinedThroughputGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "SitePage" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "site_bundle") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        this.graph.update(msg);
        this.lastGraphState = msg;
    }
}

class ShaperSiteCombinedThroughputGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Bandwidth", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();
        this.option.legend.data = [];
        this.option.xAxis.data = [];

        debugLog(data);
        let sites = data.Data.blob[0];
        let circuits = data.Data.blob[1];
        let top_5 = data.Data.blob[2];

        let series = [];
        let otherDown = [];
        let otherUp = [];
        let n = 0;

        const addEntry = (name, body) => {
            if (!this.option.xAxis.data.length) {
                this.option.xAxis.data = body.ticks || [];
            }
            const throughputMultiplier = throughputUnitToBitsPerSecondMultiplier(body?.throughput_unit);
            const down = (body.download_bytes_median || []).map((v) => (v || 0) * throughputMultiplier);
            const up = (body.upload_bytes_median || []).map((v) => 0 - ((v || 0) * throughputMultiplier));
            series.push({
                name,
                type: "line",
                data: down,
                areaStyle: {},
                itemStyle: { color: window.graphPalette[n] },
                stack: "down",
                smooth: true,
                showSymbol: false,
                xAxisIndex: 0,
                yAxisIndex: 0,
            });
            series.push({
                name,
                type: "line",
                data: up,
                areaStyle: {},
                itemStyle: { color: window.graphPalette[n] },
                stack: "up",
                smooth: true,
                showSymbol: false,
                xAxisIndex: 1,
                yAxisIndex: 0,
            });
            this.option.legend.data.push(name);
            n += 1;
        };

        const accumulateOther = (body) => {
            const throughputMultiplier = throughputUnitToBitsPerSecondMultiplier(body?.throughput_unit);
            for (let i = 0; i < (body.download_bytes_median || []).length; i++) {
                if (otherDown.length <= i) otherDown.push(0);
                if (otherUp.length <= i) otherUp.push(0);
                otherDown[i] += (body.download_bytes_median[i] || 0) * throughputMultiplier;
                otherUp[i] += 0 - ((body.upload_bytes_median[i] || 0) * throughputMultiplier);
            }
        };

        for (const [site, value] of Object.entries(sites)) {
            if (top5includes(top_5, "Site", site)) {
                addEntry(value.header.name, value.body);
            } else {
                accumulateOther(value.body);
            }
        }

        for (const [site, value] of Object.entries(circuits)) {
            if (top5includes(top_5, "Circuit", site)) {
                addEntry(value.header.name, value.body);
            } else {
                accumulateOther(value.body);
            }
        }

        series.push({
            name: "Other",
            type: "line",
            data: otherDown,
            areaStyle: {},
            itemStyle: { color: window.graphPalette[n] },
            stack: "down",
            smooth: true,
            showSymbol: false,
            xAxisIndex: 0,
            yAxisIndex: 0,
        });
        series.push({
            name: "Other",
            type: "line",
            data: otherUp,
            areaStyle: {},
            itemStyle: { color: window.graphPalette[n] },
            stack: "up",
            smooth: true,
            showSymbol: false,
            xAxisIndex: 1,
            yAxisIndex: 0,
        });
        this.option.legend.data.push("Other");
        this.option.series = series;
        applySplitDirectionAxes(this.option, {
            ticks: this.option.xAxis.data,
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatBitsPerSecondAxis,
        });

        if (isRedacted()) {
            this.option.legend.textStyle = {
                fontFamily: "Illegible"
            };
        } else {
            this.option.legend.textStyle = {};
        }

        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatBitsPerSecondAxis,
            formatter: (params) => {
                if (!params || params.length === 0) return "";
                let result = `<div style="font-size: 10px;">${formatTimeTick(params[0].axisValue)}<br/>`;
                const downloadData = params.filter((p) => p.axisIndex === 0);
                const uploadData = params.filter((p) => p.axisIndex === 1);
                if (downloadData.length > 0) {
                    result += "<strong>Download</strong><br/>";
                    downloadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatBitsPerSecondAxis(item.value)}<br/>`;
                    });
                }
                if (uploadData.length > 0) {
                    result += "<strong>Upload</strong><br/>";
                    uploadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatBitsPerSecondAxis(item.value)}<br/>`;
                    });
                }
                result += "</div>";
                return result;
            },
        });

        finalizeSplitDirectionChart(this.option);
        this.option && this.chart.setOption(this.option, true);
        debugLog(data);
    }
}

function top5includes(top_5, type, hash) {
    for (let i = 0; i < top_5.length; i++) {
        if (top_5[i][0] === type && top_5[i][1] == hash) {
            return true;
        }
    }
    return false;
}
